/**
 * Save migration.
 *
 * The first slice read a save like this:
 *
 *     if (p.version !== 1) return null;      // -> loadWorld() ?? createWorld()
 *
 * which means the next person to add a field to WorldState silently deletes
 * every playtester's county. With four agents editing state concurrently that is
 * not a hypothetical, it is a schedule.
 *
 * Rules here:
 *   - Every version bump gets a step. A version with no step is a hard stop.
 *   - A save we cannot read is quarantined, never overwritten.
 *   - Missing fields are filled, not fatal. Absent state is the normal shape of
 *     an old save; it should cost the player nothing.
 */
import { SAVE_VERSION, type WorldState } from "../sim/types";

export type MigrateResult =
  | { ok: true; world: WorldState; from: number; steps: string[] }
  | { ok: false; reason: string; from: number };

type Raw = Record<string, unknown>;

/** version N -> N+1. Keyed by the version being upgraded *from*. */
const STEPS: Record<number, { name: string; up: (p: Raw) => Raw }> = {
  1: {
    name: "1->2 cross-system layer (signals, restlessness, watch)",
    up: (p) => {
      p.signals = [];
      p.restlessness = 0;
      // Left undefined on purpose: `ensureSystems()` derives the watch snapshot
      // from the world it is handed, which is more correct than guessing here.
      p.watch = undefined;
      if (!Array.isArray(p.factions)) p.factions = [];
      return p;
    },
  },
};

/**
 * Fields added since v1 that an older save simply will not have.
 *
 * This exists so nobody has to hand-patch `loadWorld` again. Adding a field to
 * WorldState is a hourly event with four agents on the repo, and every one of
 * those `if (x === undefined) x = ...` lines was a version bump waiting to
 * happen. Declare the default here instead; it is applied on every load, at
 * every version, and costs nothing when the field is already present.
 *
 * A missing field is not a corrupt save. It is last week's save.
 */
const DEFAULTS: Array<[path: string, value: unknown]> = [
  ["player.strikeLanded", false], // combat feel-pass, lands with the lunge work
  ["restlessness", 0],
  ["signals", []],
];

function backfill(p: Raw): void {
  for (const [path, value] of DEFAULTS) {
    const keys = path.split(".");
    let node: Raw = p;
    for (let i = 0; i < keys.length - 1; i++) {
      const next = node[keys[i]!];
      if (!next || typeof next !== "object") {
        node = null as unknown as Raw;
        break;
      }
      node = next as Raw;
    }
    if (!node) continue;
    const leaf = keys[keys.length - 1]!;
    if (node[leaf] === undefined) node[leaf] = Array.isArray(value) ? [] : value;
  }
}

export function migrate(raw: unknown): MigrateResult {
  if (!raw || typeof raw !== "object") return { ok: false, reason: "not an object", from: 0 };
  const p = raw as Raw;

  const from = typeof p.version === "number" ? p.version : 0;
  if (from < 1) return { ok: false, reason: `unknown save version ${String(p.version)}`, from };
  if (from > SAVE_VERSION) {
    // A newer build wrote this. Downgrading is not a migration, it is data loss.
    return { ok: false, reason: `save is from a newer build (v${from} > v${SAVE_VERSION})`, from };
  }

  const shape = checkShape(p);
  if (shape) return { ok: false, reason: shape, from };

  const steps: string[] = [];
  let v = from;
  let cur = p;
  while (v < SAVE_VERSION) {
    const step = STEPS[v];
    if (!step) return { ok: false, reason: `no migration step from v${v}`, from };
    cur = step.up(cur);
    steps.push(step.name);
    v += 1;
  }
  cur.version = SAVE_VERSION;
  backfill(cur);

  return { ok: true, world: cur as unknown as WorldState, from, steps };
}

/**
 * Structural checks only — the things whose absence would crash the sim on the
 * first frame. Anything recoverable belongs in a step or in `ensureSystems`.
 */
function checkShape(p: Raw): string | null {
  const ow = p.overworld as Raw | undefined;
  if (!ow) return "missing overworld";
  if (typeof ow.w !== "number" || typeof ow.h !== "number") return "overworld has no size";
  if (!ow.wake) return "overworld has no wake point";
  if (!Array.isArray(ow.tiles)) return "overworld tiles are not an array";
  if ((ow.tiles as unknown[]).length !== (ow.w as number) * (ow.h as number)) {
    return "overworld tile count does not match its size";
  }
  const pl = p.player as Raw | undefined;
  if (!pl || typeof pl.x !== "number" || typeof pl.y !== "number") return "player has no position";
  if (
    (pl.x as number) < 0 ||
    (pl.y as number) < 0 ||
    (pl.x as number) > (ow.w as number) ||
    (pl.y as number) > (ow.h as number)
  ) {
    return "player is outside the map";
  }
  if (!p.home) return "missing home";
  return null;
}
